import { useEffect, useState } from 'react';
import {
  useExperimentFlags,
  useFreemiumTreatment,
} from '@hungryroot/experiments';

type UseShowFavoriteHeartResult =
  | { isReady: false }
  | { isReady: true; showHeart: boolean };

/**
 * Gate for the heart favorite icon. It shows for the Exp 242 (popsicle) variant
 * — which replaces the bookmark for variant 1 across all viewports — and for a
 * freemium treatment (Exp 255 or Exp 260), where the prospect/guest discover
 * feed ships the heart, save, and add-to-cookbook toast under its own gate.
 *
 * `isReady` is false until we can answer definitively: the experiment flags may
 * still be loading, and we wait for client mount so the server and first client
 * render agree (both render nothing). Callers render nothing while not ready so
 * neither the bookmark nor the heart flashes before the correct one is known.
 *
 * The freemium treatment is read as an `enroll: false` peek: Exp 255 enrolls
 * only at its two designated entry points (the homepage hamburger and the
 * cookbook landings), so this gate — like `useFreemiumRouteGate` — reads the
 * existing assignment without ever creating one. `enroll: false` is a no-op for
 * Exp 242, which is post-signup and enrolled by the backend.
 */
export function useShowFavoriteHeart(): UseShowFavoriteHeartResult {
  const flags = useExperimentFlags([242], { enroll: false });
  const freemium = useFreemiumTreatment({ enroll: false });

  const [isMounted, setIsMounted] = useState(false);
  useEffect(() => {
    setIsMounted(true);
  }, []);

  if (!isMounted || flags.isLoading || freemium.isLoading) {
    return { isReady: false };
  }

  return {
    isReady: true,
    showHeart: flags.popsicleExp242.isVariant || freemium.isTreatment,
  };
}
